import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../Config/axiosInstance";
import DistributionForm from "./DistributionForm";
import ReviewForm from "./ReviewForm";
import "./AddAutovehicles.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import Select from 'react-select';
import { NumericFormat } from "react-number-format";

const EditAutovehicles = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // tipuri combustibil - mutat în useMemo
    const fuelTypes = useMemo(() => [
        "Benzina",
        "Motorina",
        "GPL",
        "Hybrid",
        "Electric",
    ], []);

    // opțiuni pentru status - mutat în useMemo
    const statusOptions = useMemo(() => [
        "Open",
        "Closed"
    ], []);

    // formular - fără expire aici (mutat în contract)
    const initialFormData = useMemo(() => ({
        auto: "",
        auto_type: "",
        driver: "",
        registration_number: "",
        RCA: "",
        RCA_display: "",
        ITP: "",
        ITP_display: "",
        E_Vignette: "",
        E_Vignette_display: "",
        VIN_series: "",
        year: "",
        cylindrical_capacity: "",
        combustibile: "",
    }), []);

    // formular pentru contract - cu expire adăugat
    const initialContractData = useMemo(() => ({
        supplier: "",
        signing_date: "",
        signing_date_display: "",
        duration: "",
        start_date: "",
        start_date_display: "",
        expire: "",
        expire_display: "",
        status: "Open",
        advance_payment: "",
        monthly_installment: "",
        value: "",
        annual_mileage: "",
        replacement_days: "",
        km_pickup: "",
        km_return: "",
    }), []);

    // state-uri principale
    const [formData, setFormData] = useState(initialFormData);
    const [contractData, setContractData] = useState(initialContractData);
    const [drivers, setDrivers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const [contractFieldErrors, setContractFieldErrors] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showErrorToast, setShowErrorToast] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // State pentru validare (pentru consistență cu AddAutovehicles)
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Refs pentru a ține minte valorile inițiale
    const initialKilometersRef = useRef(null);
    const initialDistributionRef = useRef(null);
    const initialReviewRef = useRef(null);

    // state pentru distribuție și revizie
    const [distribution, setDistribution] = useState({
        distribution_data: "",
        distribution_km: "",
        distribution_interval: "",
        estimate_date_distribution: "",
        estimate_km_distribution: "",
    });

    const [review, setReview] = useState({
        review_data: "",
        review_km: "",
        review_interval: "",
        estimate_date_review: "",
        estimate_km_review: "",
    });

    // state pentru kilometraj
    const [kilometers, setKilometers] = useState({
        kilometers: "",
        recorded_date: format(new Date(), "yyyy-MM-dd"),
        recorded_date_display: format(new Date(), "dd.MM.yyyy"),
    });

    // Stiluri personalizate pentru react-select (aceleași ca în AddAutovehicles)
    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            width: '100%',
            height: '38px',
            border: state.isFocused ? '2px solid #050d15' : '1.5px solid #ccc',
            borderRadius: '8px',
            backgroundColor: 'white',
            boxShadow: state.isFocused ? '0 0 0 1px rgba(0, 0, 0, 0.2)' : 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#050d15' : '1.5px solid #ccc',
            },
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
            minHeight: '38px',
            cursor: 'pointer',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#777',
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#1a1a1a',
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
        }),
        input: (provided) => ({
            ...provided,
            margin: 0,
            padding: 0,
            fontSize: '14px',
            color: '#1a1a1a',
            fontFamily: "'Arial', sans-serif",
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0 12px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '36px',
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            color: state.isFocused ? '#1976d2' : '#555',
            padding: '8px',
            '&:hover': {
                color: '#1976d2',
            }
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: '#ddd',
            marginTop: '8px',
            marginBottom: '8px',
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 9999,
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
        }),
        menuList: (provided) => ({
            ...provided,
            padding: '4px 0',
            maxHeight: '200px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#1976d2' :
                state.isFocused ? '#f0f7ff' : 'white',
            color: state.isSelected ? 'white' : '#1a1a1a',
            padding: '8px 12px',
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
            cursor: 'pointer',
            '&:active': {
                backgroundColor: '#1976d2',
                color: 'white'
            }
        }),
        clearIndicator: (provided) => ({
            ...provided,
            color: '#666',
            padding: '8px',
            '&:hover': {
                color: '#333'
            }
        }),
    };

    // Stil pentru select-uri goale
    const emptySelectStyles = {
        ...customSelectStyles,
        singleValue: (provided) => ({
            ...provided,
            color: '#777 !important',
        }),
    };

    // Stil pentru select-uri cu eroare
    const errorSelectStyles = {
        ...customSelectStyles,
        control: (provided, state) => ({
            ...customSelectStyles.control(provided, state),
            border: '1px solid #ff5c5c !important',
            boxShadow: state.isFocused ? '0 0 0 1px rgba(255, 92, 92, 0.2)' : '0 0 0 1px rgba(255, 92, 92, 0.5)',
            '&:hover': {
                borderColor: '#ff5c5c !important',
            }
        })
    };

    // Opțiuni pentru react-select
    const driverOptions = useMemo(() =>
        drivers.map(driver => ({
            value: driver.id,
            label: `${driver.first_name} ${driver.last_name}`
        })), [drivers]);

    const supplierOptions = useMemo(() =>
        suppliers.map(supplier => ({
            value: supplier.id,
            label: supplier.name
        })), [suppliers]);

    const fuelOptions = useMemo(() =>
        fuelTypes.map(fuel => ({
            value: fuel,
            label: fuel
        })), [fuelTypes]); // Adăugat fuelTypes ca dependință

    const statusSelectOptions = useMemo(() =>
        statusOptions.map(status => ({
            value: status,
            label: status
        })), [statusOptions]); // Adăugat statusOptions ca dependință

    // Handler pentru react-select pentru driver
    const handleDriverSelect = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            driver: selectedOption ? selectedOption.value : ""
        }));
        if (isSubmitted) {
            setFieldErrors(prev => ({ ...prev, driver: "" }));
        }
    };

    // Handler pentru react-select pentru supplier
    const handleSupplierSelect = (selectedOption) => {
        setContractData(prev => ({
            ...prev,
            supplier: selectedOption ? selectedOption.value : ""
        }));
        if (isSubmitted) {
            setContractFieldErrors(prev => ({ ...prev, supplier: "" }));
        }
    };

    // Handler pentru react-select pentru combustibil
    const handleFuelSelect = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            combustibile: selectedOption ? selectedOption.value : ""
        }));
        if (isSubmitted) {
            setFieldErrors(prev => ({ ...prev, combustibile: "" }));
        }
    };

    // Handler pentru react-select pentru status
    const handleStatusSelect = (selectedOption) => {
        setContractData(prev => ({
            ...prev,
            status: selectedOption ? selectedOption.value : "Open"
        }));
    };

    // Handler special pentru NumericFormat câmpuri
    const handleNumericChange = (field) => ({ value }) => {
        setContractData((prev) => ({ ...prev, [field]: value }));
        
        if (isSubmitted) {
            setContractFieldErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    // Găsește valoarea selectată pentru driver
    const selectedDriver = driverOptions.find(option => option.value === formData.driver);
    // Găsește valoarea selectată pentru supplier
    const selectedSupplier = supplierOptions.find(option => option.value === contractData.supplier);
    // Găsește valoarea selectată pentru combustibil
    const selectedFuel = fuelOptions.find(option => option.value === formData.combustibile);
    // Găsește valoarea selectată pentru status
    const selectedStatus = statusSelectOptions.find(option => option.value === contractData.status);

    // încarcă datele mașinii
    useEffect(() => {
        const load = async () => {
            try {
                console.log("=== LOADING VEHICLE DATA ===");

                // Mai întâi obținem departamentele permise și toate datele necesare în paralel
                const [allowedDeptsResponse, driversResponse, suppliersResponse] = await Promise.all([
                    axiosInstance.get("/department/user/"),
                    axiosInstance.get("/employees/"),
                    axiosInstance.get("/suppliers/")
                ]);

                const allowedDeptIds = allowedDeptsResponse.data.map(d => d.value);

                // Filtrăm supplierii pe baza departamentelor permise (la fel ca în AddAutovehicles)
                let filteredSuppliers = suppliersResponse.data;
                if (allowedDeptIds.length > 0) {
                    filteredSuppliers = filteredSuppliers.filter(supplier =>
                        supplier.departments?.some(deptId => allowedDeptIds.includes(deptId))
                    );
                }

                setDrivers(driversResponse.data);
                setSuppliers(filteredSuppliers);

                // Continuă cu restul datelor
                const [autoResponse, allKmResponse, allDistResponse, allRevResponse] = await Promise.all([
                    axiosInstance.get(`/all-autovehicles/${id}/`),
                    axiosInstance.get("/autovehicle-kilometers/"),
                    axiosInstance.get("/distribution/"),
                    axiosInstance.get("/review/")
                ]);

                const d = autoResponse.data;

                // ADĂUGAT: Dacă supplier-ul vehiculului nu este în lista filtrați, îl adăugăm forțat
                const vehicleSupplierId = d.supplier;
                if (vehicleSupplierId) {
                    const supplierInList = filteredSuppliers.find(s => s.id === vehicleSupplierId);
                    if (!supplierInList) {
                        // Găsim supplier-ul în lista completă și îl adăugăm la lista filtrați
                        const fullSupplier = suppliersResponse.data.find(s => s.id === vehicleSupplierId);
                        if (fullSupplier) {
                            filteredSuppliers = [...filteredSuppliers, fullSupplier];
                            setSuppliers(filteredSuppliers); // Re-setăm cu supplier-ul adăugat
                        }
                    }
                }

                // Setează datele principale
                setFormData({
                    auto: d.auto,
                    auto_type: d.auto_type,
                    driver: d.driver || "",
                    registration_number: d.registration_number,
                    VIN_series: d.VIN_series,
                    cylindrical_capacity: d.cylindrical_capacity,
                    combustibile: d.combustibile,
                    year: d.year ? d.year.split("-")[0] : "",

                    RCA: d.RCA || "",
                    RCA_display: d.RCA ? format(new Date(d.RCA), "dd.MM.yyyy") : "",

                    ITP: d.ITP || "",
                    ITP_display: d.ITP ? format(new Date(d.ITP), "dd.MM.yyyy") : "",

                    E_Vignette: d.E_Vignette || "",
                    E_Vignette_display: d.E_Vignette ? format(new Date(d.E_Vignette), "dd.MM.yyyy") : "",
                });

                // === KILOMETERS ===
                // MODIFICAT: Sortează după created_at în loc de recorded_date
                const vehicleKilometers = allKmResponse.data
                    .filter(km => km.vehicle === Number(id))
                    .sort((a, b) => {
                        // Sortează descrescător după created_at
                        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                        return dateB - dateA; // Cea mai recentă prima
                    });

                const latestKm = vehicleKilometers.length > 0 ? vehicleKilometers[0] : null;

                initialKilometersRef.current = latestKm ? {
                    kilometers: latestKm.kilometers?.toString() || "",
                    recorded_date: latestKm.recorded_date || format(new Date(), "yyyy-MM-dd"),
                } : {
                    kilometers: "",
                    recorded_date: format(new Date(), "yyyy-MM-dd"),
                };

                setKilometers({
                    kilometers: latestKm ? latestKm.kilometers?.toString() || "" : "",
                    recorded_date: latestKm ? latestKm.recorded_date || format(new Date(), "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
                    recorded_date_display: latestKm ? format(new Date(latestKm.recorded_date), "dd.MM.yyyy") : format(new Date(), "dd.MM.yyyy"),
                });

                // === DISTRIBUTION ===
                const vehicleDistributions = allDistResponse.data
                    .filter(dist => dist.vehicle === Number(id))
                    .sort((a, b) => {
                        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                        return dateB - dateA;
                    });

                const latestDistribution = vehicleDistributions.length > 0 ? vehicleDistributions[0] : null;

                initialDistributionRef.current = latestDistribution ? {
                    distribution_data: latestDistribution.distribution_data || "",
                    distribution_km: latestDistribution.distribution_km?.toString() || "",
                    distribution_interval: latestDistribution.distribution_interval?.toString() || "",
                    estimate_date_distribution: latestDistribution.estimate_date_distribution || "",
                    estimate_km_distribution: latestDistribution.estimate_km_distribution?.toString() || "",
                } : {
                    distribution_data: "",
                    distribution_km: "",
                    distribution_interval: "",
                    estimate_date_distribution: "",
                    estimate_km_distribution: "",
                };

                setDistribution(latestDistribution ? {
                    distribution_data: latestDistribution.distribution_data || "",
                    distribution_km: latestDistribution.distribution_km?.toString() || "",
                    distribution_interval: latestDistribution.distribution_interval?.toString() || "",
                    estimate_date_distribution: latestDistribution.estimate_date_distribution || "",
                    estimate_km_distribution: latestDistribution.estimate_km_distribution?.toString() || "",
                } : {
                    distribution_data: "",
                    distribution_km: "",
                    distribution_interval: "",
                    estimate_date_distribution: "",
                    estimate_km_distribution: "",
                });

                // === REVIEW ===
                const vehicleReviews = allRevResponse.data
                    .filter(rev => rev.vehicle === Number(id))
                    .sort((a, b) => {
                        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                        return dateB - dateA;
                    });

                const latestReview = vehicleReviews.length > 0 ? vehicleReviews[0] : null;

                initialReviewRef.current = latestReview ? {
                    review_data: latestReview.review_data || "",
                    review_km: latestReview.review_km?.toString() || "",
                    review_interval: latestReview.review_interval?.toString() || "",
                    estimate_date_review: latestReview.estimate_date_review || "",
                    estimate_km_review: latestReview.estimate_km_review?.toString() || "",
                } : {
                    review_data: "",
                    review_km: "",
                    review_interval: "",
                    estimate_date_review: "",
                    estimate_km_review: "",
                };

                setReview(latestReview ? {
                    review_data: latestReview.review_data || "",
                    review_km: latestReview.review_km?.toString() || "",
                    review_interval: latestReview.review_interval?.toString() || "",
                    estimate_date_review: latestReview.estimate_date_review || "",
                    estimate_km_review: latestReview.estimate_km_review?.toString() || "",
                } : {
                    review_data: "",
                    review_km: "",
                    review_interval: "",
                    estimate_date_review: "",
                    estimate_km_review: "",
                });

                // Date contract
                setContractData({
                    supplier: d.supplier || "",
                    signing_date: d.signing_date || "",
                    signing_date_display: d.signing_date ? format(new Date(d.signing_date), "dd.MM.yyyy") : "",
                    duration: d.duration || "",
                    start_date: d.start_date || "",
                    start_date_display: d.start_date ? format(new Date(d.start_date), "dd.MM.yyyy") : "",
                    expire: d.expire || "",
                    expire_display: d.expire ? format(new Date(d.expire), "dd.MM.yyyy") : "",
                    status: d.status || "Open",
                    advance_payment: d.advance_payment || "",
                    monthly_installment: d.monthly_installment || "",
                    value: d.value || "",
                    annual_mileage: d.annual_mileage || "",
                    replacement_days: d.replacement_days || "",
                    km_pickup: d.km_pickup || "",
                    km_return: d.km_return || "",
                });

                console.log("=== DATA LOADED SUCCESSFULLY ===");
                console.log("Filtered suppliers count:", filteredSuppliers.length);
                console.log("Latest kilometers (by created_at):", latestKm);
                console.log("Latest distribution:", latestDistribution);
                console.log("Latest review:", latestReview);

            } catch (e) {
                console.error("LOAD ERROR", e);
                console.error("Error details:", e.response?.data);
                showError("Error loading vehicle data. Please try again.");
            }
        };
        load();
    }, [id]);

    // Funcții pentru handleChange (la fel ca în AddAutovehicles)
    const handleDatePick = (field, date) => {
        if (!date) {
            setFormData((prev) => ({
                ...prev,
                [field]: "",
                [field + "_display"]: ""
            }));

            if (isSubmitted) {
                setFieldErrors((prev) => ({ ...prev, [field]: "Required" }));
            }
            return;
        }

        const iso = format(date, "yyyy-MM-dd");
        const display = format(date, "dd.MM.yyyy");

        setFormData((prev) => ({
            ...prev,
            [field]: iso,
            [field + "_display"]: display,
        }));

        if (isSubmitted) {
            setFieldErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    const handleContractDatePick = (field, date) => {
        if (!date) {
            setContractData((prev) => ({
                ...prev,
                [field]: "",
                [field + "_display"]: ""
            }));

            if (isSubmitted && field === "expire") {
                setContractFieldErrors(prev => ({ ...prev, [field]: "Required" }));
            }
            return;
        }

        const iso = format(date, "yyyy-MM-dd");
        const display = format(date, "dd.MM.yyyy");

        setContractData((prev) => ({
            ...prev,
            [field]: iso,
            [field + "_display"]: display,
        }));

        if (isSubmitted) {
            setContractFieldErrors(prev => ({ ...prev, [field]: "" }));
        }
    };

    const handleKilometersDatePick = (date) => {
        if (!date) {
            setKilometers((prev) => ({
                ...prev,
                recorded_date: "",
                recorded_date_display: ""
            }));

            if (isSubmitted) {
                setFieldErrors((prev) => ({ ...prev, recorded_date: "Required" }));
            }
            return;
        }

        const iso = format(date, "yyyy-MM-dd");
        const display = format(date, "dd.MM.yyyy");

        setKilometers((prev) => ({
            ...prev,
            recorded_date: iso,
            recorded_date_display: display,
        }));

        if (isSubmitted) {
            setFieldErrors((prev) => ({ ...prev, recorded_date: "" }));
        }
    };

    const handleChange = (field) => (e) => {
        let value = e.target.value;

        if (field === "year") value = value.replace(/\D/g, "").slice(0, 4);
        if (field === "cylindrical_capacity") value = value.replace(/\D/g, "");

        setFormData((prev) => ({ ...prev, [field]: value }));

        if (isSubmitted) {
            setFieldErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    const handleContractChange = (field) => (e) => {
     

        let value = e.target.value;

        if (field === "annual_mileage" || field === "replacement_days" ||
            field === "km_pickup" || field === "km_return" || field === "duration") {
            value = value.replace(/\D/g, "");
        }

        setContractData((prev) => ({ ...prev, [field]: value }));

        if (isSubmitted) {
            setContractFieldErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    const handleKilometersChange = (field) => (e) => {
        let value = e.target.value;

        if (field === "kilometers") {
            value = value.replace(/\D/g, "");
        }

        setKilometers((prev) => ({ ...prev, [field]: value }));

        if (isSubmitted && fieldErrors.kilometers) {
            setFieldErrors((prev) => ({ ...prev, kilometers: "" }));
        }
    };

    // Funcții pentru verificarea schimbărilor
    const hasValueChanged = (initial, current, fields) => {
        for (const field of fields) {
            const initialVal = initial[field] || "";
            const currentVal = current[field] || "";

            if (initialVal.toString() !== currentVal.toString()) {
                return true;
            }
        }
        return false;
    };

    const hasKilometersChanged = () => {
        const initial = initialKilometersRef.current || {};
        const current = {
            kilometers: kilometers.kilometers || "",
            recorded_date: kilometers.recorded_date || ""
        };

        return hasValueChanged(initial, current, ['kilometers', 'recorded_date']);
    };

    // Funcții îmbunătățite pentru a detecta schimbări în câmpurile estimate
    const hasDistributionChanged = () => {
        const initial = initialDistributionRef.current || {};
        const current = distribution;

        // Lista tuturor câmpurilor care trebuie verificate
        const allDistributionFields = [
            'distribution_data',
            'distribution_km',
            'distribution_interval',
            'estimate_date_distribution',
            'estimate_km_distribution'
        ];

        // Verifică dacă s-a schimbat vreun câmp
        for (const field of allDistributionFields) {
            const initialVal = initial[field] || "";
            const currentVal = current[field] || "";

            // Compară ca string-uri pentru a evita probleme cu null/undefined
            if (String(initialVal).trim() !== String(currentVal).trim()) {
                console.log(`Distribution field changed: ${field}`, { initial: initialVal, current: currentVal });
                return true;
            }
        }

        return false;
    };

    const hasReviewChanged = () => {
        const initial = initialReviewRef.current || {};
        const current = review;

        // Lista tuturor câmpurilor care trebuie verificate
        const allReviewFields = [
            'review_data',
            'review_km',
            'review_interval',
            'estimate_date_review',
            'estimate_km_review'
        ];

        // Verifică dacă s-a schimbat vreun câmp
        for (const field of allReviewFields) {
            const initialVal = initial[field] || "";
            const currentVal = current[field] || "";

            // Compară ca string-uri pentru a evita probleme cu null/undefined
            if (String(initialVal).trim() !== String(currentVal).trim()) {
                console.log(`Review field changed: ${field}`, { initial: initialVal, current: currentVal });
                return true;
            }
        }

        return false;
    };

    // Funcții de validare
    const validateMainForm = () => {
        const errors = {};

        const requiredFields = [
            "auto",
            "auto_type",
            "registration_number",
            "year",
            "VIN_series",
            "cylindrical_capacity",
            "RCA",
            "ITP",
            "E_Vignette",
            "combustibile",
        ];

        requiredFields.forEach((f) => {
            if (!formData[f] || formData[f].toString().trim() === "") {
                errors[f] = "Required";
            }
        });

        if (!kilometers.kilometers || kilometers.kilometers.toString().trim() === "") {
            errors.kilometers = "Required";
        }

        if (!kilometers.recorded_date || kilometers.recorded_date.toString().trim() === "") {
            errors.recorded_date = "Required";
        }

        return errors;
    };

    const validateContractForm = () => {
        const errors = {};

        const requiredContractFields = [
            "supplier",
            "signing_date",
            "duration",
            "start_date",
            "expire",
            "advance_payment",
            "monthly_installment",
            "value",
            "annual_mileage",
            "replacement_days",
            "km_pickup",
            "km_return",
        ];

        requiredContractFields.forEach((f) => {
            const value = contractData[f];

            if (f === "advance_payment" || f === "monthly_installment" || f === "value") {
                // Convertim valoarea formatată pentru validare
                const numericValue = value ? parseFloat(value.replace(/\./g, '').replace(',', '.')) : null;
                if (!value || value.toString().trim() === "" || numericValue <= 0) {
                    errors[f] = "Required";
                }
            } else if (f === "annual_mileage" || f === "replacement_days" ||
                f === "km_pickup" || f === "km_return" || f === "duration") {
                if (!value || value.toString().trim() === "" || parseInt(value) < 0) {
                    errors[f] = "Required";
                }
            } else if (!value || value.toString().trim() === "") {
                errors[f] = "Required";
            }
        });

        return errors;
    };

    const cleanPayload = (obj) => {
        const out = {};
        for (const key in obj) {
            out[key] = obj[key] === "" ? null : obj[key];
        }
        return out;
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorToast(true);
        setTimeout(() => setShowErrorToast(false), 5000);
    };

    const shouldShowError = (fieldName, errorObj) => {
        return isSubmitted && errorObj[fieldName];
    };

    const handleSubmit = async () => {
        setIsSubmitted(true);

        const mainErrors = validateMainForm();
        const contractErrors = validateContractForm();

        setFieldErrors(mainErrors);
        setContractFieldErrors(contractErrors);

        const hasErrors = Object.keys(mainErrors).length > 0 || Object.keys(contractErrors).length > 0;

        if (hasErrors) {
            showError("Please fill in all required fields");
            return;
        }

        setIsSaving(true);

        try {
            const payload = {
                ...formData,
                driver: formData.driver ? parseInt(formData.driver) : null,
                year: formData.year ? `${formData.year}-01-01` : null,
                supplier: contractData.supplier ? parseInt(contractData.supplier) : null,
                signing_date: contractData.signing_date || null,
                duration: contractData.duration || null,
                start_date: contractData.start_date || null,
                expire: contractData.expire || null,
                status: contractData.status,
                advance_payment: contractData.advance_payment ? 
                    parseFloat(contractData.advance_payment.replace(/\./g, '').replace(',', '.')) : null,
                monthly_installment: contractData.monthly_installment ? 
                    parseFloat(contractData.monthly_installment.replace(/\./g, '').replace(',', '.')) : null,
                value: contractData.value ? 
                    parseFloat(contractData.value.replace(/\./g, '').replace(',', '.')) : null,
                annual_mileage: contractData.annual_mileage ? parseInt(contractData.annual_mileage) : null,
                replacement_days: contractData.replacement_days ? parseInt(contractData.replacement_days) : null,
                km_pickup: contractData.km_pickup ? parseInt(contractData.km_pickup) : null,
                km_return: contractData.km_return ? parseInt(contractData.km_return) : null,
            };

            console.log("Saving vehicle payload:", payload);
            await axiosInstance.put(`/all-autovehicles/${id}/`, cleanPayload(payload));

            // Salvează kilometrajul doar dacă a fost modificat
            const kilometersChanged = hasKilometersChanged();

            if (kilometers.kilometers && kilometersChanged) {
                const kilometersPayload = {
                    ...cleanPayload(kilometers),
                    vehicle: parseInt(id),
                };

                console.log("Saving NEW kilometers record:", kilometersPayload);
                await axiosInstance.post("/autovehicle-kilometers/", kilometersPayload);
            }

            // Salvează distribuția doar dacă a fost modificată
            const distributionChanged = hasDistributionChanged();

            if (distributionChanged) {
                const hasDistributionData = Object.values(distribution).some(value =>
                    value && value.toString().trim() !== ""
                );

                if (hasDistributionData) {
                    const distributionPayload = {
                        ...cleanPayload(distribution),
                        vehicle: parseInt(id),
                    };

                    console.log("Saving NEW distribution record:", distributionPayload);
                    await axiosInstance.post("/distribution/", distributionPayload);
                }
            }

            // Salvează revizia doar dacă a fost modificată
            const reviewChanged = hasReviewChanged();

            if (reviewChanged) {
                const hasReviewData = Object.values(review).some(value =>
                    value && value.toString().trim() !== ""
                );

                if (hasReviewData) {
                    const reviewPayload = {
                        ...cleanPayload(review),
                        vehicle: parseInt(id),
                    };

                    console.log("Saving NEW review record:", reviewPayload);
                    await axiosInstance.post("/review/", reviewPayload);
                }
            }

            // Actualizează ref-urile după salvare
            initialKilometersRef.current = {
                kilometers: kilometers.kilometers || "",
                recorded_date: kilometers.recorded_date || "",
            };

            initialDistributionRef.current = {
                distribution_data: distribution.distribution_data || "",
                distribution_km: distribution.distribution_km || "",
                distribution_interval: distribution.distribution_interval || "",
                estimate_date_distribution: distribution.estimate_date_distribution || "",
                estimate_km_distribution: distribution.estimate_km_distribution || "",
            };

            initialReviewRef.current = {
                review_data: review.review_data || "",
                review_km: review.review_km || "",
                review_interval: review.review_interval || "",
                estimate_date_review: review.estimate_date_review || "",
                estimate_km_review: review.estimate_km_review || "",
            };

            setShowToast(true);
            setTimeout(() => navigate("/all-autovehicles"), 1500);

        } catch (e) {
            console.error("SAVE ERROR", e);
            console.error("Error details:", e.response?.data);
            setIsSaving(false);
            showError("Error saving vehicle. Please check the data and try again.");
        }
    };

    return (
        <>
            {showToast && <div className="global-toast success">✔ Vehicle updated successfully!</div>}
            {showErrorToast && <div className="global-toast error">✗ {errorMessage}</div>}

            <div className="addautovehicles-page">
                <div className="add-autovehicle-container">

                    {/* HEADER */}
                    <div className="top-header addauto-header">
                        <div className="header-left">
                            <ArrowBackIcon className="back-icon-autovehicles" onClick={() => navigate(-1)} />
                            <h2 className="header-title-autovehicles">Edit autovehicle</h2>
                        </div>

                        <div className="header-actions">
                            <button className="cancel-btn" onClick={() => navigate(-1)}>Cancel</button>
                            <button
                                className={`save-btn ${isSaving ? "disabled" : ""}`}
                                onClick={!isSaving ? handleSubmit : undefined}
                                disabled={isSaving}
                            >
                                {isSaving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>

                    <hr className="header-divider-autovehicles" />

                    {/* FORM SECTIONS */}
                    <div className="form-sections">

                        {/* LEFT SIDE */}
                        <div className="vehicle-form">
                            <div className="form-card">

                                <div className="row1">
                                    <div className="field">
                                        <label>Auto <span className="required">*</span></label>
                                        <input
                                            value={formData.auto}
                                            placeholder="Auto name"
                                            onChange={handleChange("auto")}
                                            className={shouldShowError("auto", fieldErrors) ? "error-input" : ""}
                                        />
                                        {shouldShowError("auto", fieldErrors) && <span className="field-error">{fieldErrors.auto}</span>}
                                    </div>
                                </div>

                                <div className="row1">
                                    <div className="field">
                                        <label>Type <span className="required">*</span></label>
                                        <input
                                            value={formData.auto_type}
                                            placeholder="Auto type"
                                            onChange={handleChange("auto_type")}
                                            className={shouldShowError("auto_type", fieldErrors) ? "error-input" : ""}
                                        />
                                        {shouldShowError("auto_type", fieldErrors) && <span className="field-error">{fieldErrors.auto_type}</span>}
                                    </div>
                                </div>

                                <div className="row1">
                                    <div className="field">
                                        <label>Registration number <span className="required">*</span></label>
                                        <input
                                            value={formData.registration_number}
                                            placeholder="Ex: B 76 FGH"
                                            onChange={handleChange("registration_number")}
                                            className={shouldShowError("registration_number", fieldErrors) ? "error-input" : ""}
                                        />
                                        {shouldShowError("registration_number", fieldErrors) && <span className="field-error">{fieldErrors.registration_number}</span>}
                                    </div>
                                </div>

                                <div className="row2">
                                    <div className="field">
                                        <label>Current Km <span className="required">*</span></label>
                                        <input
                                            value={kilometers.kilometers}
                                            placeholder="Ex: 120000"
                                            onChange={handleKilometersChange("kilometers")}
                                            className={shouldShowError("kilometers", fieldErrors) ? "error-input" : ""}
                                        />
                                        {shouldShowError("kilometers", fieldErrors) && <span className="field-error">{fieldErrors.kilometers}</span>}
                                    </div>

                                    <div className="field">
                                        <label>Km Recorded Date <span className="required">*</span></label>
                                        <div className="datepicker-container">
                                            <DatePicker
                                                id="datepicker-kilometers-date"
                                                selected={kilometers.recorded_date ? new Date(kilometers.recorded_date) : null}
                                                onChange={handleKilometersDatePick}
                                                dateFormat="dd.MM.yyyy"
                                                placeholderText="dd.mm.yyyy"
                                                className={`datepicker-input-with-icon ${shouldShowError("recorded_date", fieldErrors) ? "error-input" : ""}`}
                                                calendarClassName="custom-calendar"
                                                autoComplete="off"
                                            />
                                            <span
                                                className="calendar-icon-inside"
                                                onClick={() => document.querySelector("#datepicker-kilometers-date")?.focus()}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                </svg>
                                            </span>
                                        </div>
                                        {shouldShowError("recorded_date", fieldErrors) && <span className="field-error">{fieldErrors.recorded_date}</span>}
                                    </div>
                                </div>

                                <div className="row1">
                                    <div className="field">
                                        <label>Driver</label>
                                        <Select
                                            value={selectedDriver}
                                            onChange={handleDriverSelect}
                                            options={driverOptions}
                                            placeholder="Select driver"
                                            isSearchable
                                            isClearable={true}
                                            styles={!formData.driver ? emptySelectStyles : customSelectStyles}
                                            className={`react-select-container ${shouldShowError("driver", fieldErrors) ? 'select-error' : ''}`}
                                            classNamePrefix="react-select"
                                        />
                                        {shouldShowError("driver", fieldErrors) && <span className="field-error">{fieldErrors.driver}</span>}
                                    </div>
                                </div>

                                <div className="row1">
                                    <div className="field vin-full">
                                        <label>VIN series <span className="required">*</span></label>
                                        <input
                                            value={formData.VIN_series}
                                            placeholder="Ex: TMBDC23U989016367"
                                            onChange={handleChange("VIN_series")}
                                            className={shouldShowError("VIN_series", fieldErrors) ? "error-input" : ""}
                                        />
                                        {shouldShowError("VIN_series", fieldErrors) && <span className="field-error">{fieldErrors.VIN_series}</span>}
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* RIGHT SIDE */}
                        <div className="right-side">
                            <div className="top-3-dates">

                                <div className="field">
                                    <label>Made year <span className="required">*</span></label>
                                    <input
                                        value={formData.year}
                                        maxLength={4}
                                        placeholder="Enter year (YYYY)"
                                        onChange={handleChange("year")}
                                        className={shouldShowError("year", fieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("year", fieldErrors) && <span className="field-error">{fieldErrors.year}</span>}
                                </div>

                                <div className="field">
                                    <label>Cylindrical capacity <span className="required">*</span></label>
                                    <input
                                        value={formData.cylindrical_capacity}
                                        placeholder="Engine capacity (cm³)"
                                        onChange={handleChange("cylindrical_capacity")}
                                        className={shouldShowError("cylindrical_capacity", fieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("cylindrical_capacity", fieldErrors) && <span className="field-error">{fieldErrors.cylindrical_capacity}</span>}
                                </div>

                                <div className="field">
                                    <label>Combustible <span className="required">*</span></label>
                                    <Select
                                        value={selectedFuel}
                                        onChange={handleFuelSelect}
                                        options={fuelOptions}
                                        placeholder="Select combustible type"
                                        isClearable={true}
                                        styles={shouldShowError("combustibile", fieldErrors) ? errorSelectStyles : customSelectStyles}
                                        className={`react-select-container ${shouldShowError("combustibile", fieldErrors) ? 'select-error' : ''}`}
                                        classNamePrefix="react-select"
                                    />
                                    {shouldShowError("combustibile", fieldErrors) && (
                                        <span className="field-error">{fieldErrors.combustibile}</span>
                                    )}
                                </div>

                                <div className="field">
                                    <label>RCA <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-rca"
                                            selected={formData.RCA ? new Date(formData.RCA) : null}
                                            onChange={(date) => handleDatePick("RCA", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("RCA", fieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                        />
                                        <span
                                            className="calendar-icon-inside"
                                            onClick={() => document.querySelector("#datepicker-rca")?.focus()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                        </span>
                                    </div>
                                    {shouldShowError("RCA", fieldErrors) && <span className="field-error">{fieldErrors.RCA}</span>}
                                </div>

                                <div className="field">
                                    <label>ITP <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-itp"
                                            selected={formData.ITP ? new Date(formData.ITP) : null}
                                            onChange={(date) => handleDatePick("ITP", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("ITP", fieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                        />
                                        <span
                                            className="calendar-icon-inside"
                                            onClick={() => document.querySelector("#datepicker-itp")?.focus()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                        </span>
                                    </div>
                                    {shouldShowError("ITP", fieldErrors) && <span className="field-error">{fieldErrors.ITP}</span>}
                                </div>

                                <div className="field">
                                    <label>E-Vignette <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-vignette"
                                            selected={formData.E_Vignette ? new Date(formData.E_Vignette) : null}
                                            onChange={(date) => handleDatePick("E_Vignette", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("E_Vignette", fieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                        />
                                        <span
                                            className="calendar-icon-inside"
                                            onClick={() => document.querySelector("#datepicker-vignette")?.focus()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                        </span>
                                    </div>
                                    {shouldShowError("E_Vignette", fieldErrors) && <span className="field-error">{fieldErrors.E_Vignette}</span>}
                                </div>

                            </div>

                            <div className="bottom-forms">
                                <DistributionForm
                                    data={distribution}
                                    setData={setDistribution}
                                    editMode={true}
                                />
                                <ReviewForm
                                    data={review}
                                    setData={setReview}
                                    editMode={true}
                                />
                            </div>
                        </div>
                    </div>

                    {/* CONTRACT FORM - CU EXPIRE ADAUGAT */}
                    <div className="contract-form-section">
                        <h3 className="contract-title">Contract Information</h3>

                        <div className="contract-form-grid">
                            {/* ROW 1 - SUPPLIER FULL WIDTH */}
                            <div className="contract-row-full">
                                <div className="field full-width">
                                    <label>Supplier <span className="required">*</span></label>
                                    <Select
                                        value={selectedSupplier}
                                        onChange={handleSupplierSelect}
                                        options={supplierOptions}
                                        placeholder="Select supplier"
                                        isSearchable
                                        isClearable={true}
                                        styles={shouldShowError("supplier", contractFieldErrors) ? errorSelectStyles : customSelectStyles}
                                        className={`react-select-container ${shouldShowError("supplier", contractFieldErrors) ? 'select-error' : ''}`}
                                        classNamePrefix="react-select"
                                    />
                                    {shouldShowError("supplier", contractFieldErrors) && <span className="field-error">{contractFieldErrors.supplier}</span>}
                                </div>
                            </div>

                            {/* ROW 2 - 3 COLUMNS */}
                            <div className="contract-row">
                                <div className="field">
                                    <label>Signing Date <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-signing"
                                            selected={contractData.signing_date ? new Date(contractData.signing_date) : null}
                                            onChange={(date) => handleContractDatePick("signing_date", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("signing_date", contractFieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                        />
                                        <span
                                            className="calendar-icon-inside"
                                            onClick={() => document.querySelector("#datepicker-signing")?.focus()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                        </span>
                                    </div>
                                    {shouldShowError("signing_date", contractFieldErrors) && <span className="field-error">{contractFieldErrors.signing_date}</span>}
                                </div>

                                <div className="field">
                                    <label>Duration <span className="required">*</span></label>
                                    <input
                                        value={contractData.duration}
                                        placeholder="Ex: 36 months"
                                        onChange={handleContractChange("duration")}
                                        className={shouldShowError("duration", contractFieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("duration", contractFieldErrors) && <span className="field-error">{contractFieldErrors.duration}</span>}
                                </div>

                                <div className="field">
                                    <label>Start Date <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-start"
                                            selected={contractData.start_date ? new Date(contractData.start_date) : null}
                                            onChange={(date) => handleContractDatePick("start_date", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("start_date", contractFieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                        />
                                        <span
                                            className="calendar-icon-inside"
                                            onClick={() => document.querySelector("#datepicker-start")?.focus()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                        </span>
                                    </div>
                                    {shouldShowError("start_date", contractFieldErrors) && <span className="field-error">{contractFieldErrors.start_date}</span>}
                                </div>
                            </div>

                            {/* ROW 3 - 3 COLUMNS */}
                            <div className="contract-row">
                                <div className="field">
                                    <label>Expire Date <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-expire"
                                            selected={contractData.expire ? new Date(contractData.expire) : null}
                                            onChange={(date) => handleContractDatePick("expire", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("expire", contractFieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                        />
                                        <span
                                            className="calendar-icon-inside"
                                            onClick={() => document.querySelector("#datepicker-expire")?.focus()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                        </span>
                                    </div>
                                    {shouldShowError("expire", contractFieldErrors) && <span className="field-error">{contractFieldErrors.expire}</span>}
                                </div>

                                <div className="field">
                                    <label>Status <span className="required">*</span></label>
                                    <Select
                                        value={selectedStatus}
                                        onChange={handleStatusSelect}
                                        options={statusSelectOptions}
                                        placeholder="Select status"
                                        isSearchable
                                        styles={customSelectStyles}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                    />
                                </div>

                                <div className="field">
                                    <label>Advance Payment (LEI) <span className="required">*</span></label>
                                    <NumericFormat
                                        value={contractData.advance_payment}
                                        onValueChange={handleNumericChange("advance_payment")}
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale
                                        allowNegative={false}
                                        allowLeadingZeros={false}
                                        inputMode="decimal"
                                        className={`input ${shouldShowError("advance_payment", contractFieldErrors) ? "error-input" : ""}`}
                                        placeholder="0,00"
                                        required
                                    />
                                    {shouldShowError("advance_payment", contractFieldErrors) && <span className="field-error">{contractFieldErrors.advance_payment}</span>}
                                </div>
                            </div>

                            {/* ROW 4 - 3 COLUMNS */}
                            <div className="contract-row">
                                <div className="field">
                                    <label>Monthly Installment (LEI) <span className="required">*</span></label>
                                    <NumericFormat
                                        value={contractData.monthly_installment}
                                        onValueChange={handleNumericChange("monthly_installment")}
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale
                                        allowNegative={false}
                                        allowLeadingZeros={false}
                                        inputMode="decimal"
                                        className={`input ${shouldShowError("monthly_installment", contractFieldErrors) ? "error-input" : ""}`}
                                        placeholder="0,00"
                                        required
                                    />
                                    {shouldShowError("monthly_installment", contractFieldErrors) && <span className="field-error">{contractFieldErrors.monthly_installment}</span>}
                                </div>

                                <div className="field">
                                    <label>Contract Value (LEI) <span className="required">*</span></label>
                                    <NumericFormat
                                        value={contractData.value}
                                        onValueChange={handleNumericChange("value")}
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale
                                        allowNegative={false}
                                        allowLeadingZeros={false}
                                        inputMode="decimal"
                                        className={`input ${shouldShowError("value", contractFieldErrors) ? "error-input" : ""}`}
                                        placeholder="0,00"
                                        required
                                    />
                                    {shouldShowError("value", contractFieldErrors) && <span className="field-error">{contractFieldErrors.value}</span>}
                                </div>

                                <div className="field">
                                    <label>Annual Km <span className="required">*</span></label>
                                    <input
                                        value={contractData.annual_mileage}
                                        placeholder="0"
                                        onChange={handleContractChange("annual_mileage")}
                                        className={shouldShowError("annual_mileage", contractFieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("annual_mileage", contractFieldErrors) && <span className="field-error">{contractFieldErrors.annual_mileage}</span>}
                                </div>
                            </div>

                            {/* ROW 5 - 3 COLUMNS */}
                            <div className="contract-row">
                                <div className="field">
                                    <label>Replacement Days <span className="required">*</span></label>
                                    <input
                                        value={contractData.replacement_days}
                                        placeholder="0"
                                        onChange={handleContractChange("replacement_days")}
                                        className={shouldShowError("replacement_days", contractFieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("replacement_days", contractFieldErrors) && <span className="field-error">{contractFieldErrors.replacement_days}</span>}
                                </div>

                                <div className="field">
                                    <label>Km Pickup <span className="required">*</span></label>
                                    <input
                                        value={contractData.km_pickup}
                                        placeholder="Ex: 95000"
                                        onChange={handleContractChange("km_pickup")}
                                        className={shouldShowError("km_pickup", contractFieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("km_pickup", contractFieldErrors) && <span className="field-error">{contractFieldErrors.km_pickup}</span>}
                                </div>

                                <div className="field">
                                    <label>Km Return <span className="required">*</span></label>
                                    <input
                                        value={contractData.km_return}
                                        placeholder="Ex: 95000"
                                        onChange={handleContractChange("km_return")}
                                        className={shouldShowError("km_return", contractFieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("km_return", contractFieldErrors) && <span className="field-error">{contractFieldErrors.km_return}</span>}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export default EditAutovehicles;